import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '../home/components/Header';
import { Footer } from '../home/components/Footer';

export default function TermsPage() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('section1');

  // Safely extract the dynamically translated sections 
  // Make sure your i18n JSON is correctly set up with returnObjects: true
  const sections = t('terms.sections', { returnObjects: true }) as Array<{
    id: string;
    title: string;
    blocks: any[];
  }>;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sectionNodes = document.querySelectorAll('[data-section]');
      let foundSection = '';

      for (const section of sectionNodes) {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 150 && rect.bottom >= 150) {
          foundSection = section.getAttribute('data-section') || '';
          break;
        }
      }

      if (foundSection) {
        setActiveSection(foundSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(`[data-section="${sectionId}"]`);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-yellow-400 to-orange-500 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            {t('terms.hero.title')}
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Sticky Sidebar - Table of Contents */}
            <aside className="lg:w-80 flex-shrink-0">
              <div className="lg:sticky lg:top-24 bg-gray-800 rounded-2xl p-6 border border-gray-700">
                <h2 className="text-xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
                  <i className="ri-list-check text-2xl"></i>
                  {t('terms.toc.title')}
                </h2>
                <nav className="space-y-2 max-h-[60vh] overflow-y-auto custom-scrollbar">
                  {sections && Array.isArray(sections) && sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis ${
                        activeSection === section.id
                          ? 'bg-yellow-400 text-gray-900 font-semibold'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-yellow-400'
                      }`}
                    >
                      <span className="text-sm">{section.title}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 space-y-8">
              {sections && Array.isArray(sections) && sections.map((section) => (
                <div key={section.id} data-section={section.id} className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                  <h2 className="text-2xl font-bold text-yellow-400 mb-6 flex items-center">
                    {section.title}
                  </h2>
                  
                  <div className="space-y-4 text-gray-300 text-base leading-relaxed">
                    {section.blocks.map((block, index) => {
                      if (Array.isArray(block)) {
                        return (
                          <ul key={index} className="space-y-2 ml-6">
                            {block.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <i className="ri-checkbox-blank-circle-fill text-[8px] text-yellow-400 mt-2"></i>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      
                      // For regular string paragraphs
                      return (
                        <p key={index} className="whitespace-pre-wrap">
                          {block.includes('**') ? (
                            <span dangerouslySetInnerHTML={{ __html: block.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                          ) : (
                            block
                          )}
                        </p>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Final Statement */}
              <div className="bg-gradient-to-br from-yellow-400/10 to-orange-500/10 rounded-2xl p-8 border-2 border-yellow-400">
                <p className="text-gray-300 text-center text-lg whitespace-pre-wrap">
                  {t('terms.finalStatement')}
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}